import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

const ThemeContext = createContext(null);

/*
 * Lukee käyttäjän Hallintaan tallennetun teeman.
 *
 * Tämä arvo on käyttäjän oma Hallinnan asetus.
 * Julkisilla sivuilla sitä EI käytetä sivun ulkoasuun.
 */
function getInitialTheme(config) {
  const defaultTheme = config?.theme?.defaultTheme || "dark";
  const storageKey = config?.theme?.storageKey || "paranoidi_theme";

  const saved =
    typeof window !== "undefined"
      ? localStorage.getItem(storageKey)
      : null;

  return saved === "light" || saved === "dark"
    ? saved
    : defaultTheme;
}

/*
 * Palauttaa nykyisen URL-polun ilman lopun kauttaviivaa.
 */
function getCurrentPathname() {
  if (typeof window === "undefined") {
    return "/";
  }

  return window.location.pathname.replace(/\/+$/, "") || "/";
}

/*
 * Vain nämä kirjautuneen käyttäjän sivut saavat käyttää
 * käyttäjän valitsemaa light/dark-teemaa.
 *
 * Kaikki muut sivut ovat julkista puolta ja pysyvät
 * Paranoidin kiinteässä tummassa ilmeessä.
 */
function usesAuthenticatedTheme(pathname) {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";

  return (
    path === "/admin" ||
    path.startsWith("/admin/") ||
    path === "/yhteys" ||
    path === "/taydenna-tilaustasi" ||
    path.startsWith("/taydenna-tilaustasi/")
  );
}

export function ThemeProvider({ config, children }) {
  /*
   * Tämä on käyttäjän Hallintaan tallennettu oma teema.
   * Esim. "light".
   */
  const [theme, setTheme] = useState(() => getInitialTheme(config));

  /*
   * Reitti täytyy pitää staten kautta mukana, koska Paranoidi
   * vaihtaa sivuja myös SPA-navigaatiolla ilman täyttä reloadia.
   */
  const [pathname, setPathname] = useState(getCurrentPathname);

  const storageKey =
    config?.theme?.storageKey || "paranoidi_theme";

  const authenticatedThemeEnabled =
    usesAuthenticatedTheme(pathname);

  /*
   * Kirjautuneella puolella käytetään käyttäjän valintaa.
   *
   * Julkisella puolella käytetään AINA dark-teemaa,
   * riippumatta siitä onko Hallinnassa valittu light vai dark.
   */
  const activeTheme = authenticatedThemeEnabled
    ? theme
    : "dark";

  /*
   * Seurataan Paranoidin SPA-reititystä.
   *
   * popstate:
   * - selaimen takaisin/eteen
   *
   * paranoidi:navigate:
   * - Paranoidin oma SPA-navigaatio
   */
  useEffect(() => {
    function handleNavigation() {
      setPathname(getCurrentPathname());
    }

    window.addEventListener("popstate", handleNavigation);
    window.addEventListener(
      "paranoidi:navigate",
      handleNavigation
    );

    return () => {
      window.removeEventListener(
        "popstate",
        handleNavigation
      );

      window.removeEventListener(
        "paranoidi:navigate",
        handleNavigation
      );
    };
  }, []);

  /*
   * Asetetaan HTML-elementin aktiivinen teema.
   *
   * useLayoutEffect estää sen, että esimerkiksi julkinen sivu
   * ehtisi välähtää light-teemassa ennen dark-teeman asettamista.
   *
   * pathname on riippuvuuksissa tarkoituksella:
   * myös public -> public SPA-siirtymä pakottaa oikean
   * julkisen dark-teeman uudelleen.
   */
  useLayoutEffect(() => {
    const root = document.documentElement;

    root.dataset.theme = activeTheme;

    root.classList.remove(
      "theme-dark",
      "theme-light"
    );

    root.classList.add(
      `theme-${activeTheme}`
    );
  }, [activeTheme, pathname]);

  /*
   * Tallennetaan vain käyttäjän Hallinnan oma valinta.
   *
   * Julkisen sivun pakotettu dark ei koskaan muuta
   * localStoragen paranoidi_theme-arvoa.
   */
  useEffect(() => {
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);

  const value = useMemo(
    () => ({
      /*
       * Julkisella sivulla context ilmoittaa myös dark,
       * joten useTheme()-hookkia käyttävä julkinen komponentti
       * ei vahingossa reagoi Hallinnan light-asetukseen.
       */
      theme: activeTheme,

      /*
       * Julkinen sivu ei saa muuttaa käyttäjän Hallinnan teemaa.
       */
      setTheme: (nextTheme) => {
        if (!authenticatedThemeEnabled) {
          return;
        }

        setTheme(nextTheme);
      },

      toggleTheme: () => {
        if (!authenticatedThemeEnabled) {
          return;
        }

        setTheme((currentTheme) =>
          currentTheme === "dark"
            ? "light"
            : "dark"
        );
      },

      isDark: activeTheme === "dark",
    }),
    [
      activeTheme,
      authenticatedThemeEnabled,
    ]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);

  if (!ctx) {
    throw new Error(
      "useTheme must be used inside ThemeProvider"
    );
  }

  return ctx;
}